import React, { useEffect, useRef, useState } from "react";
import { Button, Input, Popover, Spin, Tooltip } from "antd";
import { CheckOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { getTagsOptimize } from "../../Catalog/_redux/tags/tagsCrud";
import { getProductLabels, updateProductLabels } from "../../Catalog/_redux/products/productsCrud";
import CreateTags from "../../User/pages/userProfile/tag-management-settings/CreateTags";
import { handleApiErrorResponse } from "../utils";
import { hasPermissions, Permission } from "../constants";

const EMPTY_LABELS = [];
const MAX_VISIBLE_LABELS = 2;
// Popups opened inside a Bootstrap modal must render in it: its focus trap blocks inputs placed outside.
export const getLabelPopupContainer = (trigger) => {
  let container = trigger;
  while (container && !container.classList?.contains("modal")) container = container.parentElement;
  return container || document.body;
};
const pillStyle = (label) => ({
  background: label.bg_color || "#fff", color: label.color || "#000",
  padding: "2px 8px", borderRadius: 10, fontSize: 11,
  display: "inline-flex", alignItems: "center", gap: 6,
});

const applyLabelChange = (list, { add, remove }) => (add
  ? (list.some((item) => item.value === add.value) ? list : [...list, add])
  : list.filter((item) => item.value !== remove.value));

// With onChange, edit the enclosing form; otherwise save only product assignments.
export default function ProductLabelEditor({ tags = EMPTY_LABELS, productId, onChange, onSaved, screen }) {
  const [labels, setLabels] = useState(tags || EMPTY_LABELS);
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const busy = useRef(false);
  const rootRef = useRef(null);
  const popupOpen = useRef(false);
  const pendingSaved = useRef(null);
  const canEdit = hasPermissions([Permission.ProductUpdate])[0];
  const canCreate = hasPermissions([Permission.TagUpdate])[0];
  useEffect(() => { setLabels(tags || EMPTY_LABELS); }, [tags]);

  // A parent refresh remounts this row, so hold onSaved until every popup is closed.
  useEffect(() => {
    popupOpen.current = open || moreOpen || creating;
    if (popupOpen.current || !pendingSaved.current) return;
    const saved = pendingSaved.current;
    pendingSaved.current = null;
    onSaved?.(saved);
  }, [open, moreOpen, creating]);

  useEffect(() => {
    if (!productId || onChange) return;
    const updated = (event) => {
      if (event.detail.productId === productId) setLabels(event.detail.tags || EMPTY_LABELS);
    };
    window.addEventListener("product-labels-updated", updated);
    return () => window.removeEventListener("product-labels-updated", updated);
  }, [productId, onChange]);

  const loadOptions = async () => {
    const { data } = await getTagsOptimize({ tag_type: 1, status: 1, page_size: -1 });
    setOptions(data.entities || []);
  };
  const openPicker = async (visible) => {
    setOpen(visible);
    if (visible) setMoreOpen(false);
    if (!visible) return;
    setLoading(true);
    try {
      await loadOptions();
      if (!onChange && productId) {
        // Fresh read so a save never overwrites labels changed elsewhere since this row loaded.
        const { data } = await getProductLabels(productId);
        setLabels(data.entity.tags || []);
      }
    } catch (error) { handleApiErrorResponse(error); }
    finally { setLoading(false); }
  };
  // change is { add: label } or { remove: label }.
  const changeLabels = async (change) => {
    if (busy.current || loading) return;
    if (onChange) {
      const next = applyLabelChange(labels, change);
      setLabels(next);
      onChange(next);
      return;
    }
    busy.current = true;
    setSaving(true);
    try {
      // Apply only this change to the server's current labels: this row may be stale (edited in
      // the product form, another tab or by another user), and must never drop those labels.
      const { data: current } = await getProductLabels(productId);
      const next = applyLabelChange(current.entity.tags || [], change);
      const { data } = await updateProductLabels(productId, next.map((label) => label.id));
      const saved = data.entity.tags || [];
      setLabels(saved);
      window.dispatchEvent(new CustomEvent("product-labels-updated", { detail: { productId, tags: saved } }));
      if (popupOpen.current) pendingSaved.current = saved;
      else onSaved?.(saved);
    } catch (error) { handleApiErrorResponse(error); }
    finally { busy.current = false; setSaving(false); }
  };
  const toggle = (label) => changeLabels(labels.some((item) => item.value === label.value) ? { remove: label } : { add: label });

  // Visibility changes presentation only; saves retain the full assignment list.
  const allowedOnScreen = (label) => !screen || label.external_details?.[`show_on_${screen}`] === true;
  // On a screen, only labels enabled for it are offered; hidden assignments stay saved.
  const pickerOptions = options.filter(allowedOnScreen);
  const visibleLabels = labels.filter((assigned) => {
    return allowedOnScreen(options.find((item) => item.value === assigned.value) || assigned);
  });
  const inlineLabels = visibleLabels.slice(0, MAX_VISIBLE_LABELS);
  const overflowLabels = visibleLabels.slice(MAX_VISIBLE_LABELS);
  const renderLabel = (assigned) => {
      const label = options.find((item) => item.value === assigned.value) || assigned;
      return <Tooltip key={label.value} title={label.description}>
        <span style={pillStyle(label)}>{label.value}
          {canEdit && <button type="button" aria-label={`Remove label ${label.value}`} disabled={saving || loading}
            onClick={() => changeLabels({ remove: label })}
            style={{ border: 0, padding: 0, background: "transparent", color: "inherit", cursor: "pointer", lineHeight: 1 }}>×</button>}
        </span>
      </Tooltip>;
  };
  return <div ref={rootRef} onClick={(event) => event.stopPropagation()} style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4, whiteSpace: "normal" }}>
    {inlineLabels.map(renderLabel)}
    {overflowLabels.length > 0 && <Popover trigger="click" placement="bottomLeft"
      visible={moreOpen} onVisibleChange={(visible) => { setMoreOpen(visible); if (visible) setOpen(false); }}
      getPopupContainer={getLabelPopupContainer} zIndex={1060}
      content={<div style={{ minWidth: 220, maxHeight: 240, overflowY: "auto" }}>
        {overflowLabels.map((label) => <div key={label.value} style={{ padding: "6px 4px" }}>{renderLabel(label)}</div>)}
      </div>}>
      <button type="button" style={{ background: "#f0f0f0", color: "#EA1E51", border: 0, borderRadius: 10, padding: "2px 6px", fontSize: 11, cursor: "pointer", whiteSpace: "nowrap" }}>
        +{overflowLabels.length} more
      </button>
    </Popover>}
    {canEdit && <Popover trigger="click" placement="bottomLeft" visible={open} onVisibleChange={openPicker}
      getPopupContainer={getLabelPopupContainer}
      zIndex={1060}
      overlayClassName="tag-popover" content={<div style={{ minWidth: 240 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <strong>Select Label</strong>
          {canCreate && <Button type="link" size="small" style={{ color: "#EA1E51" }} onClick={() => { setOpen(false); setCreating(true); }}>Create New Label</Button>}
        </div>
        <Input placeholder="Search labels" value={search} onChange={(event) => setSearch(event.target.value)} />
        <Spin spinning={loading || saving}>
          <div style={{ maxHeight: 220, overflowY: "auto", marginTop: 6 }}>
            {pickerOptions.filter((label) => label.value.toLowerCase().includes(search.toLowerCase())).map((label) => {
              const selected = labels.some((item) => item.value === label.value);
              return <button key={label.id} type="button" role="checkbox" aria-checked={selected} disabled={loading || saving}
                onClick={() => toggle(label)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "6px 8px", border: 0, borderRadius: 8, cursor: "pointer", background: selected ? "#f0faff" : "#fff", marginBottom: 6 }}>
                <Tooltip title={label.description}><span style={pillStyle(label)}>{label.value}</span></Tooltip>
                {selected && <CheckOutlined style={{ color: "#1890ff" }} />}
              </button>;
            })}
            {!loading && !pickerOptions.length && <div>{screen ? `No labels enabled for ${screen}.` : "No product labels configured."}</div>}
          </div>
        </Spin>
      </div>}>
      <Button type="link" size="small" aria-label="Add label" disabled={saving} icon={<PlusCircleOutlined />}
        style={{ padding: 0, height: "auto", fontSize: 12, color: saving ? undefined : "#EA1E51" }}>
        {!visibleLabels.length && "Add Label"}
      </Button>
    </Popover>}
    {creating && <CreateTags open={creating} defaultTagType={1} defaultValues={screen ? { [`show_on_${screen}`]: true } : undefined}
      getContainer={() => getLabelPopupContainer(rootRef.current)}
      onClose={() => setCreating(false)}
      onSuccess={async () => { await openPicker(true); }} />}
  </div>;
}
